import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import { useQuery } from "@tanstack/react-query";
import {
  deviceListResponseSchema,
  type DeviceStatus,
  type DeviceSummary,
} from "@remote-android/contracts";
import { Link, Navigate, Route, Routes, useParams, useSearchParams } from "react-router-dom";

import { WebRtcViewer } from "./webrtc-viewer.js";

const statusPresentation: Record<DeviceStatus, { label: string; dot: string; badge: string }> = {
  AVAILABLE: {
    label: "Available",
    dot: "bg-emerald-400",
    badge: "border-emerald-400/20 bg-emerald-400/10 text-emerald-300",
  },
  IN_USE: {
    label: "In use",
    dot: "bg-violet-400",
    badge: "border-violet-400/20 bg-violet-400/10 text-violet-300",
  },
  CONNECTING: {
    label: "Connecting",
    dot: "bg-sky-400",
    badge: "border-sky-400/20 bg-sky-400/10 text-sky-300",
  },
  OFFLINE: {
    label: "Offline",
    dot: "bg-slate-500",
    badge: "border-slate-500/20 bg-slate-500/10 text-slate-400",
  },
  DEGRADED: {
    label: "Degraded",
    dot: "bg-amber-400",
    badge: "border-amber-400/20 bg-amber-400/10 text-amber-300",
  },
  MAINTENANCE: {
    label: "Maintenance",
    dot: "bg-orange-400",
    badge: "border-orange-400/20 bg-orange-400/10 text-orange-300",
  },
  RESERVED: {
    label: "Reserved",
    dot: "bg-cyan-400",
    badge: "border-cyan-400/20 bg-cyan-400/10 text-cyan-300",
  },
};

const devicePrefixes = ["BRI", "DANA", "OVO", "TEST"] as const;
type AuthenticationStatus = "loading" | "authenticated" | "anonymous";

function csrfCookieName(): string {
  return import.meta.env.PROD ? "__Host-remote_csrf" : "remote_csrf_dev";
}

export function readCookieValue(name: string, cookies = document.cookie): string | null {
  const prefix = `${encodeURIComponent(name)}=`;
  const cookie = cookies
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
}

function accessTokenFromResponse(body: unknown): string | null {
  if (
    typeof body !== "object" ||
    body === null ||
    !("accessToken" in body) ||
    typeof body.accessToken !== "string"
  ) {
    return null;
  }
  return body.accessToken;
}

export async function refreshSession(): Promise<string | null> {
  const csrfToken = readCookieValue(csrfCookieName());
  if (!csrfToken) {
    return null;
  }
  const response = await fetch("/api/v1/auth/refresh", {
    method: "POST",
    credentials: "include",
    headers: {
      accept: "application/json",
      "x-csrf-token": csrfToken,
    },
  });
  if (!response.ok) {
    return null;
  }
  return accessTokenFromResponse((await response.json()) as unknown);
}

async function revokeSession(): Promise<void> {
  const csrfToken = readCookieValue(csrfCookieName());
  if (!csrfToken) {
    return;
  }
  await fetch("/api/v1/auth/logout", {
    method: "POST",
    credentials: "include",
    headers: {
      "x-csrf-token": csrfToken,
    },
  });
}

function accessTokenRefreshDelay(accessToken: string): number {
  try {
    const payloadPart = accessToken.split(".")[1];
    if (!payloadPart) {
      return 8 * 60 * 1_000;
    }
    const normalized = payloadPart.replace(/-/gu, "+").replace(/_/gu, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    const payload = JSON.parse(atob(padded)) as unknown;
    if (
      typeof payload === "object" &&
      payload !== null &&
      "exp" in payload &&
      typeof payload.exp === "number"
    ) {
      return Math.max(5_000, payload.exp * 1_000 - Date.now() - 60_000);
    }
  } catch {
    // Fall back to a conservative interval for opaque or malformed tokens.
  }
  return 8 * 60 * 1_000;
}

function createDevelopmentDevices(): readonly DeviceSummary[] {
  return Array.from({ length: 20 }, (_, index) => {
    const number = index + 1;
    const prefix = devicePrefixes[Math.floor(index / 5)] ?? "TEST";
    const statuses: readonly DeviceStatus[] = [
      "AVAILABLE",
      "AVAILABLE",
      "AVAILABLE",
      "IN_USE",
      "OFFLINE",
    ];
    const status = statuses[index % statuses.length] ?? "OFFLINE";

    return {
      id: `00000000-0000-4000-8000-${number.toString().padStart(12, "0")}`,
      alias: `${prefix}-${((index % 5) + 1).toString().padStart(2, "0")}`,
      group: prefix,
      model: index % 4 === 0 ? "Pixel 8a" : "Redmi 14C",
      manufacturer: index % 4 === 0 ? "Google" : "Xiaomi",
      androidVersion: index % 4 === 0 ? "15" : "14",
      status,
      batteryLevel: Math.max(8, 96 - index * 4),
      agentName: "office-gateway-01",
      site: "main-office",
      lastSeenAt: status === "OFFLINE" ? "2026-09-20T03:42:00.000Z" : new Date().toISOString(),
      capabilities: {
        video: status !== "OFFLINE",
        control: status !== "OFFLINE",
        clipboard: false,
        audio: false,
      },
    };
  });
}

async function fetchDevices(accessToken: string | null): Promise<readonly DeviceSummary[]> {
  if (import.meta.env.DEV) {
    return createDevelopmentDevices();
  }

  const response = await fetch("/api/v1/devices", {
    credentials: "include",
    headers: {
      accept: "application/json",
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
    },
  });
  if (!response.ok) {
    throw new Error("Inventory perangkat tidak dapat dimuat");
  }
  return deviceListResponseSchema.parse(await response.json()).items;
}

function Icon({
  children,
  className = "size-5",
}: {
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      {children}
    </svg>
  );
}

function AppShell({
  children,
  onLogout,
}: {
  readonly children: ReactNode;
  readonly onLogout: () => void;
}) {
  return (
    <div className="min-h-screen bg-[#070a12] text-slate-100">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -left-72 -top-72 size-[42rem] rounded-full bg-violet-600/10 blur-[130px]" />
        <div className="absolute -right-72 top-1/3 size-[38rem] rounded-full bg-cyan-500/8 blur-[140px]" />
      </div>
      <header className="relative z-10 border-b border-white/6 bg-[#080b14]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-5 lg:px-8">
          <Link className="group flex items-center gap-3" to="/devices">
            <span className="grid size-9 place-items-center rounded-xl border border-violet-400/25 bg-violet-500/12 text-violet-300 shadow-[0_0_30px_rgba(139,92,246,0.12)]">
              <Icon>
                <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
                <path d="M10 5h4M11 18.5h2" />
              </Icon>
            </span>
            <span>
              <span className="block text-sm font-semibold tracking-wide text-white">
                Remote Android
              </span>
              <span className="block text-[10px] font-medium tracking-[0.16em] text-slate-500 uppercase">
                Operations platform
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            {import.meta.env.DEV ? (
              <span className="hidden rounded-full border border-amber-300/15 bg-amber-300/8 px-3 py-1 text-[11px] font-semibold tracking-wide text-amber-300 sm:inline">
                DEVELOPMENT MOCK
              </span>
            ) : null}
            <button
              aria-label="Keluar dari akun"
              className="flex items-center gap-2 rounded-xl border border-white/8 bg-white/[0.035] p-1.5 pr-3 text-sm transition hover:border-white/15 hover:bg-white/[0.06]"
              onClick={onLogout}
              type="button"
            >
              <span className="grid size-7 place-items-center rounded-lg bg-gradient-to-br from-violet-400 to-indigo-600 text-[11px] font-bold text-white">
                OP
              </span>
              <span className="hidden text-slate-300 sm:block">Keluar</span>
            </button>
          </div>
        </div>
      </header>
      <main className="relative z-10 mx-auto max-w-[1600px] px-5 py-7 lg:px-8 lg:py-9">
        {children}
      </main>
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  readonly label: string;
  readonly value: number;
  readonly accent: string;
}) {
  return (
    <div className="rounded-2xl border border-white/7 bg-white/[0.025] px-4 py-3.5">
      <div className="flex items-center gap-2">
        <span className={`size-1.5 rounded-full ${accent}`} />
        <span className="text-xs font-medium text-slate-500">{label}</span>
      </div>
      <p className="mt-1.5 text-2xl font-semibold tracking-tight text-white">{value}</p>
    </div>
  );
}

function DeviceCard({ device }: { readonly device: DeviceSummary }) {
  const presentation = statusPresentation[device.status];
  const available = device.status === "AVAILABLE";

  return (
    <article className="device-card group relative overflow-hidden rounded-2xl border border-white/7 bg-[#0c101b]/90 p-4">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-300/20 to-transparent opacity-0 transition group-hover:opacity-100" />
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="relative grid size-11 shrink-0 place-items-center rounded-xl border border-white/8 bg-white/[0.035] text-slate-400">
            <Icon>
              <rect x="7" y="2.5" width="10" height="19" rx="2.4" />
              <path d="M10 5h4M11 18.5h2" />
            </Icon>
            <span
              className={`absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full border-2 border-[#0c101b] ${presentation.dot}`}
            />
          </div>
          <div className="min-w-0">
            <h2 className="truncate text-[15px] font-semibold tracking-wide text-white">
              {device.alias}
            </h2>
            <p className="mt-0.5 truncate text-xs text-slate-500">{device.model}</p>
          </div>
        </div>
        <span
          className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-semibold tracking-wide ${presentation.badge}`}
        >
          {presentation.label}
        </span>
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 border-y border-white/6 py-4">
        <div>
          <dt className="text-[10px] font-medium tracking-wider text-slate-600 uppercase">Group</dt>
          <dd className="mt-1 text-xs font-medium text-slate-300">{device.group ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-[10px] font-medium tracking-wider text-slate-600 uppercase">
            Android
          </dt>
          <dd className="mt-1 text-xs font-medium text-slate-300">
            {device.androidVersion ?? "—"}
          </dd>
        </div>
        <div>
          <dt className="text-[10px] font-medium tracking-wider text-slate-600 uppercase">
            Battery
          </dt>
          <dd className="mt-1 flex items-center gap-1.5 text-xs font-medium text-slate-300">
            <span
              className={`size-1.5 rounded-full ${
                (device.batteryLevel ?? 0) < 20 ? "bg-rose-400" : "bg-emerald-400"
              }`}
            />
            {device.batteryLevel === null ? "—" : `${device.batteryLevel}%`}
          </dd>
        </div>
        <div>
          <dt className="text-[10px] font-medium tracking-wider text-slate-600 uppercase">Site</dt>
          <dd className="mt-1 truncate text-xs font-medium text-slate-300">{device.site}</dd>
        </div>
      </dl>

      <div className="mt-4 flex gap-2">
        <Link
          aria-disabled={!available}
          className={`flex h-9 flex-1 items-center justify-center gap-2 rounded-lg text-xs font-semibold transition ${
            available
              ? "bg-violet-500 text-white shadow-[0_8px_24px_rgba(139,92,246,0.2)] hover:bg-violet-400"
              : "pointer-events-none bg-white/5 text-slate-600"
          }`}
          to={`/devices/${device.id}/control`}
        >
          <Icon className="size-3.5">
            <path d="m5 3 14 8-6 2-2 6L5 3Z" />
          </Icon>
          Control
        </Link>
        <Link
          aria-disabled={device.status === "OFFLINE"}
          className={`flex h-9 flex-1 items-center justify-center gap-2 rounded-lg border text-xs font-semibold transition ${
            device.status !== "OFFLINE"
              ? "border-white/10 bg-white/[0.035] text-slate-300 hover:border-white/20 hover:bg-white/[0.07]"
              : "pointer-events-none border-white/5 text-slate-700"
          }`}
          to={`/devices/${device.id}/view`}
        >
          <Icon className="size-3.5">
            <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
            <circle cx="12" cy="12" r="2.5" />
          </Icon>
          View
        </Link>
      </div>
    </article>
  );
}

function DeviceDashboard({
  accessToken,
  onLogout,
}: {
  readonly accessToken: string | null;
  readonly onLogout: () => void;
}) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | DeviceStatus>("ALL");
  const devices = useQuery({
    queryKey: ["devices", accessToken],
    queryFn: () => fetchDevices(accessToken),
    refetchInterval: import.meta.env.DEV ? false : 5_000,
  });
  const filteredDevices = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return (devices.data ?? []).filter(
      (device) =>
        (statusFilter === "ALL" || device.status === statusFilter) &&
        (!normalizedSearch ||
          [device.alias, device.model, device.group]
            .filter((value): value is string => value !== null)
            .some((value) => value.toLowerCase().includes(normalizedSearch))),
    );
  }, [devices.data, search, statusFilter]);
  const counts = useMemo(
    () => ({
      total: devices.data?.length ?? 0,
      available: devices.data?.filter((device) => device.status === "AVAILABLE").length ?? 0,
      inUse: devices.data?.filter((device) => device.status === "IN_USE").length ?? 0,
      offline: devices.data?.filter((device) => device.status === "OFFLINE").length ?? 0,
    }),
    [devices.data],
  );

  return (
    <AppShell onLogout={onLogout}>
      <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
        <div>
          <p className="text-xs font-semibold tracking-[0.18em] text-violet-400 uppercase">
            Device inventory
          </p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-white">Android devices</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
            Pilih perangkat yang tersedia untuk memulai sesi terisolasi.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:min-w-[460px]">
          <Stat accent="bg-slate-400" label="Total" value={counts.total} />
          <Stat accent="bg-emerald-400" label="Available" value={counts.available} />
          <Stat accent="bg-violet-400" label="In use" value={counts.inUse} />
          <Stat accent="bg-slate-600" label="Offline" value={counts.offline} />
        </div>
      </div>

      <div className="mt-8 flex flex-col gap-3 rounded-2xl border border-white/7 bg-white/[0.02] p-3 sm:flex-row">
        <label className="relative flex-1">
          <span className="sr-only">Cari perangkat</span>
          <Icon className="absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-slate-600">
            <circle cx="11" cy="11" r="6.5" />
            <path d="m16 16 4 4" />
          </Icon>
          <input
            className="h-10 w-full rounded-xl border border-white/7 bg-[#080b14] pr-4 pl-10 text-sm text-slate-200 outline-none placeholder:text-slate-700 focus:border-violet-400/40 focus:ring-2 focus:ring-violet-400/10"
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search alias, model, or group..."
            type="search"
            value={search}
          />
        </label>
        <label>
          <span className="sr-only">Filter status</span>
          <select
            className="h-10 min-w-44 rounded-xl border border-white/7 bg-[#080b14] px-3 text-sm text-slate-300 outline-none focus:border-violet-400/40"
            onChange={(event) => setStatusFilter(event.target.value as "ALL" | DeviceStatus)}
            value={statusFilter}
          >
            <option value="ALL">All statuses</option>
            {Object.entries(statusPresentation).map(([status, presentation]) => (
              <option key={status} value={status}>
                {presentation.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {devices.isPending ? (
        <div className="grid place-items-center py-28 text-sm text-slate-500">
          Memuat inventory perangkat...
        </div>
      ) : devices.isError ? (
        <div className="mt-6 rounded-2xl border border-rose-400/15 bg-rose-400/8 p-5 text-sm text-rose-300">
          {devices.error.message}
        </div>
      ) : filteredDevices.length === 0 ? (
        <div className="grid place-items-center py-28 text-sm text-slate-500">
          Tidak ada perangkat yang cocok.
        </div>
      ) : (
        <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
          {filteredDevices.map((device) => (
            <DeviceCard device={device} key={device.id} />
          ))}
        </div>
      )}
    </AppShell>
  );
}

interface LoginResponse {
  readonly accessToken: string;
}

function LoginPage({
  onAuthenticated,
}: {
  readonly onAuthenticated: (accessToken: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const response = await fetch(challengeId ? "/api/v1/auth/mfa/verify" : "/api/v1/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(challengeId ? { challengeId, code: totpCode } : { email, password }),
      });
      const body = (await response.json()) as unknown;
      if (response.status === 202) {
        const challenge = body as { challengeId?: unknown };
        if (typeof challenge.challengeId !== "string") {
          throw new Error("Respons MFA tidak valid.");
        }
        setChallengeId(challenge.challengeId);
        return;
      }
      if (!response.ok) {
        throw new Error("Email, password, atau kode verifikasi tidak valid.");
      }
      const result = body as Partial<LoginResponse>;
      if (typeof result.accessToken !== "string") {
        throw new Error("Respons autentikasi tidak valid.");
      }
      onAuthenticated(result.accessToken);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Login tidak dapat diproses.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-[#070a12] px-5 py-10 text-slate-100">
      <div className="pointer-events-none absolute -top-80 left-1/2 size-[50rem] -translate-x-1/2 rounded-full bg-violet-600/12 blur-[150px]" />
      <div className="relative w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <span className="grid size-14 place-items-center rounded-2xl border border-violet-400/25 bg-violet-500/12 text-violet-300 shadow-[0_0_50px_rgba(139,92,246,0.18)]">
            <Icon className="size-7">
              <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
              <path d="M10 5h4M11 18.5h2" />
            </Icon>
          </span>
        </div>
        <div className="rounded-3xl border border-white/8 bg-[#0c101b]/90 p-7 shadow-2xl backdrop-blur-xl sm:p-9">
          <p className="text-xs font-semibold tracking-[0.18em] text-violet-400 uppercase">
            Internal access
          </p>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight text-white">
            {challengeId ? "Verifikasi MFA" : "Masuk ke portal"}
          </h1>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            {challengeId
              ? "Masukkan kode dari aplikasi authenticator."
              : "Gunakan akun operator yang diberikan administrator."}
          </p>

          <form className="mt-7 space-y-4" onSubmit={(event) => void submit(event)}>
            {challengeId ? (
              <label className="block">
                <span className="mb-2 block text-xs font-medium text-slate-400">
                  Authentication code
                </span>
                <input
                  autoComplete="one-time-code"
                  autoFocus
                  className="h-12 w-full rounded-xl border border-white/8 bg-[#070a12] px-4 text-center font-mono text-lg tracking-[0.4em] text-white outline-none focus:border-violet-400/50 focus:ring-2 focus:ring-violet-400/10"
                  inputMode="numeric"
                  maxLength={8}
                  onChange={(event) => setTotpCode(event.target.value.replace(/\D/gu, ""))}
                  required
                  value={totpCode}
                />
              </label>
            ) : (
              <>
                <label className="block">
                  <span className="mb-2 block text-xs font-medium text-slate-400">Email</span>
                  <input
                    autoComplete="username"
                    className="h-12 w-full rounded-xl border border-white/8 bg-[#070a12] px-4 text-sm text-white outline-none placeholder:text-slate-700 focus:border-violet-400/50 focus:ring-2 focus:ring-violet-400/10"
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    type="email"
                    value={email}
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-xs font-medium text-slate-400">Password</span>
                  <input
                    autoComplete="current-password"
                    className="h-12 w-full rounded-xl border border-white/8 bg-[#070a12] px-4 text-sm text-white outline-none focus:border-violet-400/50 focus:ring-2 focus:ring-violet-400/10"
                    maxLength={1024}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    type="password"
                    value={password}
                  />
                </label>
              </>
            )}

            {error ? (
              <p
                className="rounded-xl border border-rose-400/15 bg-rose-400/8 px-4 py-3 text-xs text-rose-300"
                role="alert"
              >
                {error}
              </p>
            ) : null}

            <button
              className="h-12 w-full rounded-xl bg-violet-500 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(139,92,246,0.22)] transition hover:bg-violet-400 disabled:cursor-wait disabled:opacity-60"
              disabled={submitting}
              type="submit"
            >
              {submitting ? "Memproses..." : challengeId ? "Verifikasi" : "Masuk"}
            </button>
          </form>
        </div>
        <p className="mt-5 text-center text-[11px] text-slate-600">
          Aktivitas administratif dicatat untuk keamanan.
        </p>
      </div>
    </div>
  );
}

function DeviceViewer({
  accessToken,
  onLogout,
}: {
  readonly accessToken: string;
  readonly onLogout: () => void;
}) {
  const { deviceId, mode } = useParams<{ deviceId: string; mode: string }>();
  const [searchParameters] = useSearchParams();
  const viewerMode = mode === "control" ? "control" : "view";
  return (
    <AppShell onLogout={onLogout}>
      <div className="mb-5 flex items-center justify-between gap-4">
        <div>
          <Link className="text-xs font-medium text-violet-400 hover:text-violet-300" to="/devices">
            ← Kembali ke devices
          </Link>
          <h1 className="mt-2 text-xl font-semibold text-white">
            {viewerMode === "control" ? "Control session" : "View session"}
          </h1>
        </div>
        {viewerMode === "view" ? (
          <span className="rounded-full border border-sky-400/15 bg-sky-400/8 px-3 py-1.5 text-xs font-medium text-sky-300">
            Read only
          </span>
        ) : null}
      </div>
      {deviceId ? (
        <WebRtcViewer
          accessToken={accessToken}
          deviceId={deviceId}
          forceRelay={searchParameters.get("relay") === "1"}
          mode={viewerMode}
        />
      ) : (
        <div className="rounded-xl border border-rose-400/20 bg-rose-400/10 p-4 text-rose-300">
          Device ID tidak valid.
        </div>
      )}
    </AppShell>
  );
}

function SessionLoading() {
  return (
    <div className="grid min-h-screen place-items-center bg-[#070a12] text-slate-100">
      <div className="text-center">
        <span className="mx-auto block size-8 animate-spin rounded-full border-2 border-violet-400/20 border-t-violet-400" />
        <p className="mt-4 text-sm text-slate-500">Memulihkan sesi aman...</p>
      </div>
    </div>
  );
}

export function App() {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [authenticationStatus, setAuthenticationStatus] = useState<AuthenticationStatus>(
    import.meta.env.DEV ? "authenticated" : "loading",
  );
  const authenticated = import.meta.env.DEV || authenticationStatus === "authenticated";

  const applyRefreshResult = useCallback((refreshedAccessToken: string | null) => {
    setAccessToken(refreshedAccessToken);
    setAuthenticationStatus(refreshedAccessToken ? "authenticated" : "anonymous");
  }, []);

  useEffect(() => {
    if (import.meta.env.DEV) {
      return;
    }
    let active = true;
    void refreshSession()
      .then((refreshedAccessToken) => {
        if (active) {
          applyRefreshResult(refreshedAccessToken);
        }
      })
      .catch(() => {
        if (active) {
          applyRefreshResult(null);
        }
      });
    return () => {
      active = false;
    };
  }, [applyRefreshResult]);

  useEffect(() => {
    if (import.meta.env.DEV || authenticationStatus !== "authenticated" || !accessToken) {
      return;
    }
    const refreshTimer = window.setTimeout(() => {
      void refreshSession()
        .then(applyRefreshResult)
        .catch(() => applyRefreshResult(null));
    }, accessTokenRefreshDelay(accessToken));
    return () => window.clearTimeout(refreshTimer);
  }, [accessToken, applyRefreshResult, authenticationStatus]);

  const handleAuthenticated = useCallback((newAccessToken: string) => {
    setAccessToken(newAccessToken);
    setAuthenticationStatus("authenticated");
  }, []);

  const handleLogout = useCallback(() => {
    setAuthenticationStatus("loading");
    void revokeSession().finally(() => {
      setAccessToken(null);
      setAuthenticationStatus("anonymous");
    });
  }, []);

  if (authenticationStatus === "loading") {
    return <SessionLoading />;
  }

  return (
    <Routes>
      <Route
        element={
          accessToken ? (
            <Navigate replace to="/devices" />
          ) : (
            <LoginPage onAuthenticated={handleAuthenticated} />
          )
        }
        path="/login"
      />
      <Route
        element={
          authenticated ? (
            <DeviceDashboard accessToken={accessToken} onLogout={handleLogout} />
          ) : (
            <Navigate replace to="/login" />
          )
        }
        path="/devices"
      />
      <Route
        element={
          authenticated ? (
            accessToken ? (
              <DeviceViewer accessToken={accessToken} onLogout={handleLogout} />
            ) : (
              <Navigate replace to="/login" />
            )
          ) : (
            <Navigate replace to="/login" />
          )
        }
        path="/devices/:deviceId/:mode"
      />
      <Route element={<Navigate replace to={authenticated ? "/devices" : "/login"} />} path="*" />
    </Routes>
  );
}
